/* eslint-disable @sail/data-no-forbidden-imports */
import type {GraphQlDocument} from '@sail/data';

import type {
  V1LazyQueryReturn,
  V1MutationReturn,
} from '@stripe-internal/data-v1/types';
import {merge} from 'lodash-es';
import React from 'react';
import {updatedRequirementErrorsBetaHeaderApolloOptions} from '../../updatedRequirementErrorsBetaHeaderApolloOptions';

export function useV1MutationWithRequirementsBetaHeader(
  mutationState: V1MutationReturn<GraphQlDocument<any, any, any>>,
): V1MutationReturn<GraphQlDocument<any, any, any>> {
  const [callback, state] = mutationState;
  const wrappedCallback: V1MutationReturn<GraphQlDocument<any, any, any>>[0] =
    React.useCallback(
      (
        callbackOptions?: Parameters<
          V1MutationReturn<GraphQlDocument<any, any, any>>[0]
        >[0],
      ) => {
        const options = merge(
          callbackOptions,
          updatedRequirementErrorsBetaHeaderApolloOptions,
        );
        return callback(options);
      },
      [callback],
    );

  return [wrappedCallback, state];
}

export function useV1LazyQueryWithRequirementsBetaHeader<
  Document extends GraphQlDocument<any, any, any>,
  LazyQueryReturn extends V1LazyQueryReturn<Document>,
>(lazyQueryState: LazyQueryReturn): LazyQueryReturn {
  const [callback] = lazyQueryState;
  const newLazyQueryState: LazyQueryReturn = [...lazyQueryState];

  newLazyQueryState[0] = React.useCallback(
    (
      // TypeScript determines the args type to be `any` implicitly, potentially
      // due to a conditional type in `V1LazyQueryReturn`. It would be best to
      // strongly type these args, but since the function is simple enough we
      // are allowing `any` here.
      queryOptions?: any,
    ) => {
      const options = merge(
        {},
        queryOptions,
        updatedRequirementErrorsBetaHeaderApolloOptions,
      );
      return callback(options);
    },
    [callback],
  );

  return newLazyQueryState;
}
